import { useReducer } from 'react';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import {
  DeleteSectionGroupMutation,
  UpsertSectionGroupMutation,
} from '../mutations';
import { SectionGroupsQuery } from '../queries';

const initialSectionGroupsState = {};

const sectionGroupsReducer = (sectionGroups, action) => {
  switch (action.type) {
    case 'replaceAll': {
      return _.keyBy(action.sectionGroups, 'id');
    }
    case 'add': {
      return {
        ...sectionGroups,
        [action.sectionGroup.id]: action.sectionGroup,
      };
    }
    case 'remove': {
      const newGroups = { ...sectionGroups };
      delete newGroups[action.id];
      return newGroups;
    }
    case 'update': {
      return {
        ...sectionGroups,
        [action.sectionGroup.id]: action.sectionGroup,
      };
    }
    case 'reset': {
      return initialSectionGroupsState;
    }
    default:
      throw new Error(`Invalid type: ${action.type}.`);
  }
};

const useSectionGroups = () => {
  const [sectionGroups, dispatch] = useReducer(
    sectionGroupsReducer,
    initialSectionGroupsState,
  );

  const [upsertRemoteSectionGroup] = useMutation(UpsertSectionGroupMutation);
  const [deleteRemoteSectionGroup] = useMutation(DeleteSectionGroupMutation);

  const updateSectionGroup = (id, farmId, update) => {
    const oldSectionGroup = sectionGroups[id];
    if (!oldSectionGroup) {
      return Promise.reject(
        new Error("Can't update a section group that no longer exists."),
      );
    }
    dispatch({
      type: 'update',
      sectionGroup: { ...oldSectionGroup, ...update },
    });
    return upsertRemoteSectionGroup({
      variables: {
        input: { id, farmId, updatedAt: oldSectionGroup.updatedAt, ...update },
      },
      refetchQueries: [{ query: SectionGroupsQuery, variables: { farmId } }],
      awaitRefetchQueries: true,
    }).then((response) => {
      const { result } = response.data?.upsertSectionGroup || {};
      // Sets the latest updatedAt timestamp from the server
      if (result) {
        dispatch({ type: 'update', sectionGroup: result });
      }
      return Promise.resolve(response);
    });
  };

  const removeSectionGroup = (id, farmId) => {
    const updatedAt = sectionGroups[id]?.updatedAt;
    dispatch({ type: 'remove', id });
    return deleteRemoteSectionGroup({
      variables: { input: { id, updatedAt } },
      refetchQueries: [{ query: SectionGroupsQuery, variables: { farmId } }],
      awaitRefetchQueries: true,
    });
  };

  const createSectionGroup = (sectionGroup) => {
    const { farmId } = sectionGroup;
    return upsertRemoteSectionGroup({
      variables: {
        input: {
          ...sectionGroup,
        },
      },
      refetchQueries: [{ query: SectionGroupsQuery, variables: { farmId } }],
      awaitRefetchQueries: true,
    }).then((response) => {
      const { result } = response.data?.upsertSectionGroup || {};
      if (result) {
        dispatch({ type: 'add', sectionGroup: result });
      }
      return Promise.resolve(response);
    });
  };

  const replaceSectionGroups = (newSectionGroups) => {
    dispatch({ type: 'replaceAll', sectionGroups: newSectionGroups });
  };

  const resetSectionGroups = () => dispatch({ type: 'reset' });

  return {
    sectionGroups: Object.values(sectionGroups),
    createSectionGroup, // Create a new section group.
    replaceSectionGroups, // Replace all section groups with the new list.
    removeSectionGroup, // Remove a section group by id.
    updateSectionGroup, // Update a section group by id.
    getSectionGroup: (id) => sectionGroups[id], // Get a section group by id.
    resetSectionGroups, // Reset state to initial state
    sectionGroupsHash: sectionGroups,
  };
};

export default useSectionGroups;
